import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { PopUpIcon, CloseIcon } from '~components/icons'
import { DocumentPreviewDialog } from '~pages/pages-behind-login/documents/document-preview-dialog'
import { COLORS } from '~theme/colors'
import { fileLinkSx, fileItemContainerSx } from '../../styles'

interface AttachedFileCommon {
  id: string
  name: string
  fileSize: string
}

export interface RemarkBlockProps {
  label?: string
  value: string | null | undefined
  attachedFiles?: AttachedFileCommon[]
  emptyText?: string
  variant?: 'compact' | 'default'
}

export function RemarkBlock({
  label,
  value,
  attachedFiles = [],
  emptyText = '-',
  variant = 'default',
}: RemarkBlockProps) {
  const isEmpty = !value || value === '<p><br></p>' || value === '<p></p>'
  const processedValue = value
    ? value.replace(/<p><\/p>/g, '<br/>').replace(/&nbsp;|&#160;/g, ' ')
    : ''
  const contentRef = useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const [previewFileId, setPreviewFileId] = useState<string | null>(null)
  const [isExpandedOpen, setIsExpandedOpen] = useState(false)

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const checkOverflow = () => {
      setHasOverflow(el.scrollHeight > el.clientHeight)
    }

    checkOverflow()

    const observer = new ResizeObserver(checkOverflow)
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  const baseContentSx = {
    border: '1px solid',
    borderColor: COLORS.greys.light,
    borderRadius: 0.5,
    overflowY: 'auto',
    backgroundColor: COLORS.greys.ultraLight,
    '@media print': {
      height: hasOverflow ? 'auto' : undefined,
      overflow: hasOverflow ? 'visible' : undefined,
    },
  } as const

  const dialogTitle = label ?? 'Remarks'

  return (
    <Box
      sx={
        variant === 'compact'
          ? {
              border: '1px solid',
              borderColor: COLORS.greys.light,
              borderRadius: 1,
              p: 1.5,
              minWidth: 0,
              overflow: 'hidden',
              '@media print': {
                overflow: hasOverflow ? 'visible' : undefined,
              },
            }
          : undefined
      }
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: label ? 'space-between' : 'flex-end',
          alignItems: 'center',
          mb: 1,
        }}
      >
        {label && (
          <Typography
            variant="label"
            component="label"
            sx={{ color: COLORS.greys.dark }}
          >
            {label}
          </Typography>
        )}
        {hasOverflow && (
          <Tooltip title="Expand">
            <IconButton
              size="small"
              onClick={() => setIsExpandedOpen(true)}
              sx={{ p: 0.25 }}
            >
              <PopUpIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box
        ref={contentRef}
        sx={{
          ...baseContentSx,
          p: variant === 'compact' ? 1 : 2,
          ...(variant === 'compact'
            ? { height: '150px' }
            : { minHeight: '200px', maxHeight: '400px' }),
        }}
      >
        {isEmpty ? (
          <Typography variant="paragraph2" sx={{ color: COLORS.greys.dark }}>
            {emptyText}
          </Typography>
        ) : (
          <Typography
            component="div"
            variant="paragraph2"
            sx={{
              '& p': { margin: 0 },
              '& ul, & ol': { margin: '8px 0', paddingLeft: '24px' },
              '& li': { marginBottom: '2px' },
            }}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: Render trusted rich text remarks from Quill.
            dangerouslySetInnerHTML={{ __html: processedValue }}
          />
        )}
      </Box>
      {attachedFiles.length > 0 && (
        <Box sx={{ mt: variant === 'compact' ? 1 : 2 }}>
          {variant === 'default' && (
            <Typography
              variant="label"
              component="label"
              sx={{ color: COLORS.greys.dark, display: 'block', mb: 1 }}
            >
              Attached Files
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {attachedFiles.map((file) => (
              <Box key={file.id} sx={fileItemContainerSx}>
                <Box
                  component="span"
                  onClick={() => setPreviewFileId(file.id)}
                  title={`${file.name} (${file.fileSize})`}
                  sx={{ ...fileLinkSx, cursor: 'pointer' }}
                >
                  {file.name} ({file.fileSize})
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {previewFileId && (
        <DocumentPreviewDialog
          documentId={previewFileId}
          onClose={() => setPreviewFileId(null)}
        />
      )}

      <Dialog
        open={isExpandedOpen}
        onClose={() => setIsExpandedOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {dialogTitle}
          <IconButton
            size="small"
            onClick={() => setIsExpandedOpen(false)}
            sx={{ ml: 2 }}
          >
            <CloseIcon color={COLORS.secondary.darkBlue} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: '1px solid',
              borderColor: COLORS.greys.light,
              borderRadius: 0.5,
              p: 2,
              backgroundColor: COLORS.greys.ultraLight,
              maxHeight: '60vh',
              overflowY: 'auto',
            }}
          >
            {isEmpty ? (
              <Typography
                variant="paragraph2"
                sx={{ color: COLORS.greys.dark }}
              >
                {emptyText}
              </Typography>
            ) : (
              <Typography
                component="div"
                variant="paragraph2"
                sx={{
                  '& p': { margin: 0 },
                  '& ul, & ol': { margin: '8px 0', paddingLeft: '24px' },
                  '& li': { marginBottom: '2px' },
                }}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: Render trusted rich text remarks from Quill.
                dangerouslySetInnerHTML={{ __html: processedValue }}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}
